import { useEffect, useRef } from 'react'
import { LOCK_ROUTE } from '@constants/routes'
import { sessionStore } from '@features/auth/services/sessionStore'
import { preferencesStore } from '@features/preferences/services/preferencesStore'
import { type IdleLockHandle, startIdleLock } from '@services/idleLock'

/**
 * 認証中のみ idle-lock を起動するエフェクト。
 *
 * - sessionStore.subscribe で set / clear / lock を検知
 * - preferencesStore.subscribe で idleLockMs 変更を検知 → handle を rebuild
 * - ログイン直後: 旧 handle を stop してから新 handle を起動 (re-login 時の leak 防止)
 * - ログアウト / ロック時: handle を stop して null クリア
 * - unmount 時: subscribe 解除 + 進行中 handle を stop
 *
 * idleMs は preferencesStore.getResolved().idleLockMs を都度参照する (Track D)。
 * preferences 未 load の段階では DEFAULT_PREFERENCES.idleLockMs (30 分) で動作。
 *
 * L-15 教訓: handle は useRef で保持し、再ログイン / unmount 時に旧インスタンスを
 * 必ず stop する (module-level state を使わない)。
 */
export const useSessionIdleLock = (): void => {
  const handleRef = useRef<IdleLockHandle | null>(null)

  useEffect(() => {
    const stopHandle = (): void => {
      if (handleRef.current) {
        handleRef.current.stop()
        handleRef.current = null
      }
    }

    const sync = (): void => {
      if (sessionStore.isAuthenticated()) {
        stopHandle()

        const idleMs = preferencesStore.getResolved().idleLockMs

        handleRef.current = startIdleLock({
          idleMs,
          onLock: () => {
            // T-C3: idle 満了時は LOCKED へ遷移 (旧 clear() = ログアウトと意味的に区別)。
            // SK だけ zeroize、accessToken + material は保持し、LockPage で MP 再入力で復帰。
            // SESSION_LIFECYCLE.md §5.1 参照。
            sessionStore.lock()
            window.location.hash = LOCK_ROUTE
          },
        })
      } else {
        stopHandle()
      }
    }

    sync()

    const unsubscribeSession = sessionStore.subscribe(sync)
    // preferences の idleLockMs が変わったら timer を作り直す
    const unsubscribePrefs = preferencesStore.subscribe(sync)

    return () => {
      unsubscribeSession()
      unsubscribePrefs()
      stopHandle()
    }
  }, [])
}
